import { supabase } from '../lib/supabase';

/**
 * 모든 사용자 목록을 가져오는 함수 (관리자용) - 최종 수정된 버전
 * auth.users.id를 profiles.id로 매핑하여 올바른 이력서 조회가 가능하도록 함
 */
export const getAllUsers = async () => {
  try {
    // 1. 기존 RPC 호출로 사용자 목록과 통계 정보 가져오기
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_all_users_for_admin');
    
    if (rpcError) {
      console.error('RPC 오류:', rpcError);
      throw rpcError;
    }
    
    console.log('RPC 원본 데이터:', rpcData);
    
    if (!rpcData || rpcData.length === 0) {
      return [];
    }
    
    // 2. 전체 이력서를 가져와서 이메일-profiles.id 매핑 테이블 생성
    const { data: allResumes, error: resumesError } = await supabase
      .from('resumes')
      .select('user_id, email')
      .not('email', 'is', null)
      .not('email', 'eq', '');
    
    if (resumesError) {
      console.error('이력서 조회 오류:', resumesError);
      // 에러가 있어도 기존 RPC 데이터로 대체
      return rpcData.map(user => ({
        id: user.user_id, // auth.users.id 사용 (비록 틀릴 수 있음)
        email: user.email,
        name: user.display_name || '이름 없음',
        avatar_url: user.avatar_url,
        created_at: user.created_at,
        updated_at: user.updated_at,
        email_confirmed_at: user.email_confirmed_at,
        last_sign_in_at: user.last_sign_in_at,
        profile_data: user.profile_data,
        sessionStats: {
          totalSessions: user.session_stats?.total_sessions || 0,
          completedSessions: user.session_stats?.completed_sessions || 0,
          activeSessions: user.session_stats?.active_sessions || 0,
          pausedSessions: user.session_stats?.paused_sessions || 0,
          abandonedSessions: user.session_stats?.abandoned_sessions || 0,
          lastSessionDate: user.session_stats?.last_activity,
          averageProgress: Math.round(user.session_stats?.average_progress || 0)
        }
      }));
    }
    
    console.log('이력서 기반 이메일-profiles.id 매핑:', allResumes);
    
    // 3. 이메일로 profiles.id 매핑 테이블 생성
    const emailToProfilesId = {};
    allResumes.forEach(resume => {
      if (resume.email && resume.user_id) {
        emailToProfilesId[resume.email] = resume.user_id;
      }
    });
    
    console.log('이메일-profiles.id 매핑 테이블:', emailToProfilesId);
    
    // 4. RPC 데이터에 올바른 profiles.id 매핑
    return rpcData.map(rpcUser => {
      const correctProfilesId = emailToProfilesId[rpcUser.email];
      
      if (correctProfilesId) {
        console.log(`✅ 이메일 ${rpcUser.email}: auth.users.id(${rpcUser.user_id}) -> profiles.id(${correctProfilesId})`);
        
        return {
          id: correctProfilesId, // 올바른 profiles.id 사용!
          email: rpcUser.email,
          name: rpcUser.display_name || '이름 없음',
          avatar_url: rpcUser.avatar_url,
          created_at: rpcUser.created_at,
          updated_at: rpcUser.updated_at,
          email_confirmed_at: rpcUser.email_confirmed_at,
          last_sign_in_at: rpcUser.last_sign_in_at,
          profile_data: rpcUser.profile_data,
          sessionStats: {
            totalSessions: rpcUser.session_stats?.total_sessions || 0,
            completedSessions: rpcUser.session_stats?.completed_sessions || 0,
            activeSessions: rpcUser.session_stats?.active_sessions || 0,
            pausedSessions: rpcUser.session_stats?.paused_sessions || 0,
            abandonedSessions: rpcUser.session_stats?.abandoned_sessions || 0,
            lastSessionDate: rpcUser.session_stats?.last_activity,
            averageProgress: Math.round(rpcUser.session_stats?.average_progress || 0)
          }
        };
      } else {
        console.warn(`⚠️ 이메일 ${rpcUser.email}: profiles.id를 찾을 수 없음. auth.users.id 사용`);
        
        return {
          id: rpcUser.user_id, // 대체로 auth.users.id 사용
          email: rpcUser.email,
          name: rpcUser.display_name || '이름 없음',
          avatar_url: rpcUser.avatar_url,
          created_at: rpcUser.created_at,
          updated_at: rpcUser.updated_at,
          email_confirmed_at: rpcUser.email_confirmed_at,
          last_sign_in_at: rpcUser.last_sign_in_at,
          profile_data: rpcUser.profile_data,
          sessionStats: {
            totalSessions: rpcUser.session_stats?.total_sessions || 0,
            completedSessions: rpcUser.session_stats?.completed_sessions || 0,
            activeSessions: rpcUser.session_stats?.active_sessions || 0,
            pausedSessions: rpcUser.session_stats?.paused_sessions || 0,
            abandonedSessions: rpcUser.session_stats?.abandoned_sessions || 0,
            lastSessionDate: rpcUser.session_stats?.last_activity,
            averageProgress: Math.round(rpcUser.session_stats?.average_progress || 0)
          }
        };
      }
    });
    
  } catch (error) {
    console.error('사용자 목록 가져오기 실패:', error);
    throw error;
  }
};

/**
 * 특정 사용자의 상세 정보를 가져오는 함수 (관리자용)
 */
export const getUserDetails = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        consulting_sessions (
          id,
          name,
          status,
          current_step,
          progress_percentage,
          started_at,
          completed_at,
          last_activity_at,
          total_messages_count,
          user_satisfaction_score,
          session_duration_minutes
        ),
        resumes (
          id,
          name,
          title,
          created_at,
          updated_at,
          is_active
        )
      `)
      .eq('id', userId)
      .single();
    
    if (error) throw error;
    return data;
    
  } catch (error) {
    console.error('사용자 상세 정보 가져오기 실패:', error);
    throw error;
  }
};

/**
 * 사용자 통계 요약을 가져오는 함수 (관리자용)
 */
export const getUserStats = async () => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_stats_for_admin');
    
    if (error) throw error;
    
    return {
      totalUsers: data.total_users || 0,
      usersWithProfiles: data.users_with_profiles || 0,
      activeSessions: data.active_sessions || 0,
      completedSessions: data.completed_sessions || 0, // 진행률 100% 달성
      inProgressSessions: data.in_progress_sessions || 0, // 진행 중인 세션
      newUsersThisWeek: data.new_users_this_week || 0,
      newUsersToday: data.new_users_today || 0
    };
    
  } catch (error) {
    console.error('사용자 통계 가져오기 실패:', error);
    throw error;
  }
};

/**
 * 특정 사용자의 이력서 목록을 가져오는 함수 (관리자용)
 */
/**
 * auth.users와 profiles 테이블의 관계를 확인하는 함수
 */
export const debugAuthProfileRelation = async () => {
  try {
    console.log('=== auth.users와 profiles 관계 확인 ===');
    
    // 1. RPC 함수의 실제 반환값 확인
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_all_users_for_admin');
    
    console.log('RPC get_all_users_for_admin 결과:', { data: rpcData, error: rpcError });
    
    if (rpcData && rpcData.length > 0) {
      console.log('RPC에서 반환한 첫 번째 사용자:', rpcData[0]);
      console.log('RPC user_id:', rpcData[0].user_id);
    }
    
    // 2. profiles 테이블에서 직접 사용자 목록 가져오기
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('*')
      .limit(5);
    
    console.log('profiles 테이블 직접 조회:', { data: profilesData, error: profilesError });
    
    if (profilesData && profilesData.length > 0) {
      console.log('profiles 테이블의 첫 번째 사용자:', profilesData[0]);
      console.log('profile ID:', profilesData[0].id);
    }
    
    return {
      rpcData,
      profilesData
    };
    
  } catch (error) {
    console.error('auth-profile 관계 확인 실패:', error);
    throw error;
  }
};

/**
 * profiles 테이블을 기준으로 사용자 목록을 가져오는 함수 (수정된 버전)
 */
export const getAllUsersFixed = async () => {
  try {
    console.log('getAllUsersFixed: profiles 테이블 기준으로 사용자 목록 조회');
    
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        consulting_sessions (
          id,
          name,
          status,
          progress_percentage,
          started_at,
          completed_at,
          last_activity_at
        )
      `)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('profiles 기준 사용자 목록 조회 실패:', error);
      throw error;
    }
    
    console.log('profiles 기준 사용자 목록:', data);
    
    // 데이터 가공
    return data.map(user => {
      const sessionStats = {
        totalSessions: user.consulting_sessions?.length || 0,
        completedSessions: user.consulting_sessions?.filter(s => s.status === 'completed').length || 0,
        activeSessions: user.consulting_sessions?.filter(s => s.status === 'active').length || 0,
        pausedSessions: user.consulting_sessions?.filter(s => s.status === 'paused').length || 0,
        abandonedSessions: user.consulting_sessions?.filter(s => s.status === 'abandoned').length || 0,
        lastSessionDate: user.consulting_sessions?.[0]?.last_activity_at,
        averageProgress: user.consulting_sessions?.length > 0 
          ? Math.round(user.consulting_sessions.reduce((sum, s) => sum + (s.progress_percentage || 0), 0) / user.consulting_sessions.length)
          : 0
      };
      
      return {
        id: user.id, // profiles.id를 사용
        email: user.email,
        name: user.name || '이름 없음',
        avatar_url: user.avatar_url,
        created_at: user.created_at,
        updated_at: user.updated_at,
        email_confirmed_at: user.created_at, // profiles에는 이 필드가 없으므로 대체
        last_sign_in_at: user.updated_at, // 대체값
        profile_data: user,
        sessionStats
      };
    });
    
  } catch (error) {
    console.error('profiles 기준 사용자 목록 가져오기 실패:', error);
    throw error;
  }
};

/**
 * 데이터베이스 스키마 확인용 함수들
 */
export const debugDatabaseStructure = async () => {
  try {
    // 1. resumes 테이블의 스키마 확인
    console.log('=== resumes 테이블 구조 확인 ===');
    const { data: resumesSchema, error: resumesError } = await supabase
      .from('resumes')
      .select('*')
      .limit(1);
    
    // 2. profiles 테이블의 스키마 확인
    const { data: profilesSchema, error: profilesError } = await supabase
      .from('profiles')
      .select('*')
      .limit(1);
    
    
    // 3. consulting_sessions 테이블도 확인
    const { data: sessionsSchema, error: sessionsError } = await supabase
      .from('consulting_sessions')
      .select('*')
      .limit(1);
    
    
    return {
      resumesSchema,
      profilesSchema,
      sessionsSchema
    };
    
  } catch (error) {
    console.error('데이터베이스 구조 확인 실패:', error);
    throw error;
  }
};

/**
 * 디버깅용: 모든 이력서 목록 조회
 */
export const getAllResumes = async () => {
  try {
    const { data, error } = await supabase
      .from('resumes')
      .select(`
        id,
        user_id,
        name,
        email,
        title,
        created_at
      `)
      .order('created_at', { ascending: false })
      .limit(20);
    
    console.log('전체 이력서 목록:', { data, error });
    
    if (error) throw error;
    return data || [];
    
  } catch (error) {
    console.error('전체 이력서 목록 가져오기 실패:', error);
    throw error;
  }
};

export const getUserResumes = async (userId) => {
  try {
    //console.log('getUserResumes 호출 - userId:', userId);
    
    const { data, error } = await supabase
      .from('resumes')
      .select(`
        id,
        user_id,
        name,
        email,
        phone,
        title,
        summary,
        version,
        is_active,
        created_at,
        updated_at,
        session_id,
        consulting_sessions (
          progress_percentage,
          deleted_at
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    console.log('Supabase 응답:', { data, error });
    
    if (error) {
      console.error('Supabase 에러:', error);
      throw error;
    }
    
    // 세션 표시 상태를 위한 배경색 가져오기
    const getRowClassName = (resume) => {
      if (resume.deleted_at) {
        return 'bg-red-50 hover:bg-red-100';
      }
      return 'hover:bg-gray-50';
    };
    
    const resumesWithProgress = (data || []).map(resume => ({
      ...resume,
      session_progress: resume.consulting_sessions?.progress_percentage || 0,
      deleted_at: resume.consulting_sessions?.deleted_at || null
    }));
    
    console.log('반환될 데이터 (진행률 및 삭제일 포함):', resumesWithProgress);
    return resumesWithProgress;
    
  } catch (error) {
    console.error('사용자 이력서 목록 가져오기 실패:', error);
    throw error;
  }
};

/**
 * 특정 이력서의 상세 정보를 가져오는 함수 (관리자용)
 */
export const getResumeDetails = async (resumeId) => {
  try {
    const { data, error } = await supabase
      .from('resumes')
      .select(`
        *,
        consulting_sessions (
          id,
          name,
          status,
          created_at,
          updated_at
        ),
        profiles (
          name,
          email
        )
      `)
      .eq('id', resumeId)
      .single();
    
    if (error) throw error;
    return data;
    
  } catch (error) {
    console.error('이력서 상세 정보 가져오기 실패:', error);
    throw error;
  }
};

export default {
  getAllUsers,
  getUserDetails,
  getUserStats,
  getUserResumes,
  getResumeDetails,
  getAllResumes,
  debugDatabaseStructure,
  debugAuthProfileRelation,
  getAllUsersFixed
};
